import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const GDPRPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 bg-gray-50" style={{ paddingTop: '120px', paddingBottom: '80px' }}>
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">GDPR Compliance</h1>
            <p className="text-sm text-gray-500 mb-8">Last updated: February 26, 2026</p>

            <div className="prose prose-gray max-w-none">
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Our Commitment to GDPR</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  AirDelayClaim is fully committed to complying with the General Data Protection Regulation (GDPR). 
                  We respect your privacy rights and are transparent about how we collect, process, and protect 
                  your personal data.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Legal Basis for Processing</h2>
                <p className="text-gray-600 leading-relaxed mb-4">We process your personal data based on:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li><strong>Contract Performance:</strong> Processing necessary to fulfill our service agreement with you</li>
                  <li><strong>Legal Obligation:</strong> Processing required by law (e.g., tax records, regulatory compliance)</li>
                  <li><strong>Legitimate Interest:</strong> Processing for our legitimate business interests (e.g., fraud prevention)</li>
                  <li><strong>Consent:</strong> Processing based on your explicit consent (e.g., marketing communications)</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Your GDPR Rights</h2>
                <p className="text-gray-600 leading-relaxed mb-4">Under GDPR, you have the following rights:</p>
                
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-1">Right to Access (Article 15)</h3>
                    <p className="text-gray-600 text-sm">Request a copy of all personal data we hold about you.</p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-1">Right to Rectification (Article 16)</h3>
                    <p className="text-gray-600 text-sm">Request correction of inaccurate or incomplete data.</p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-1">Right to Erasure (Article 17)</h3>
                    <p className="text-gray-600 text-sm">Request deletion of your personal data ("right to be forgotten").</p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-1">Right to Restrict Processing (Article 18)</h3>
                    <p className="text-gray-600 text-sm">Request limitation of how we process your data.</p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-1">Right to Data Portability (Article 20)</h3>
                    <p className="text-gray-600 text-sm">Receive your data in a structured, machine-readable format.</p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-1">Right to Object (Article 21)</h3>
                    <p className="text-gray-600 text-sm">Object to processing based on legitimate interests or direct marketing.</p>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">4. How to Exercise Your Rights</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  To exercise any of your GDPR rights, please submit a request to our Data Protection Officer:
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700"><strong>Email:</strong> info@airdelayclaim.com</p>
                  <p className="text-gray-700"><strong>Response Time:</strong> Within 30 days of receiving your request</p>
                  <p className="text-gray-700"><strong>Verification:</strong> We may need to verify your identity before processing</p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Protection Measures</h2>
                <p className="text-gray-600 leading-relaxed mb-4">We implement robust security measures including:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>256-bit SSL/TLS encryption for data in transit</li>
                  <li>AES-256 encryption for data at rest</li>
                  <li>Regular security audits and penetration testing</li>
                  <li>Access controls and authentication protocols</li>
                  <li>Employee training on data protection</li>
                  <li>Incident response procedures</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">6. International Data Transfers</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  When we transfer data outside the EEA, we ensure appropriate safeguards are in place, 
                  including Standard Contractual Clauses (SCCs) approved by the European Commission.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Supervisory Authority</h2>
                <p className="text-gray-600 leading-relaxed">
                  You have the right to lodge a complaint with a supervisory authority. In the UK, this is the 
                  Information Commissioner's Office (ICO):{' '}
                  <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    ico.org.uk
                  </a>
                </p>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <Link 
                to="/" 
                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default GDPRPage;
